/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useEffect, useRef } from 'react';
import { useFocusRing, useHover, usePress } from 'react-aria';
import {
  type ContextValue,
  Provider,
  TextArea as TextAreaCore,
  type TextAreaProps as TextAreaCoreProps,
  useContextProps,
  useRenderProps
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';

interface TextAreaRenderProps {
  isHovered: boolean;
  isFocused: boolean;
  isFocusVisible: boolean;
  isDisabled: boolean;
  isInvalid: boolean;
}

interface TextAreaStyleProps {
  /** The size variant of the text area. (Default: 48) */
  size?: keyof typeof sizeVariants;
  /** The variant of the text area. (Default: outlined) */
  variant?: keyof typeof variantVariants;
  /** Used to add accessories to the text area. */
  children?: ((values: {
    isHovered: boolean;
    isFocused: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
    isInvalid: boolean;
    defaultChildren: ReactNode | undefined;
  }) => ReactNode);
  style?: StyleOrFunction<TextAreaRenderProps> | {
    base?: StyleOrFunction<TextAreaRenderProps>;
    textArea?: StyleOrFunction<TextAreaRenderProps>;
  };
}

type TextAreaProps = TextAreaStyleProps & Omit<TextAreaCoreProps, 'style' | 'className' | 'children'>;

const TextAreaStyleContext = createContext<ContextValue<TextAreaProps, HTMLDivElement>>({});

const TextArea = forwardRef((props: TextAreaProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TextAreaStyleContext);

  const { style, size = 48, variant = 'outlined', children, onScroll, ...coreProps } = props;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { hoverProps, isHovered } = useHover(coreProps);
  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    isTextInput: true,
    autoFocus: coreProps.autoFocus,
    within: true,
  });
  // onKeyDown prevents spaces from being entered into the input field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pressProps: { onKeyDown, ...pressProps } } = usePress({
    onPressStart: () => textAreaRef.current?.focus(),
    preventFocusOnPress: true,
  });

  const isInvalid = !!coreProps['aria-invalid'] && coreProps['aria-invalid'] !== 'false';

  const renderState: TextAreaRenderProps = {
    isHovered,
    isFocused,
    isFocusVisible,
    isDisabled: coreProps.disabled || false,
    isInvalid,
  };

  const renderProps = useRenderProps({
    ...coreProps,
    values: renderState,
    defaultChildren: (
      <TextAreaCore
        disabled={coreProps.disabled}
        ref={textAreaRef}
        rows={1}
        onScroll={(event) => {
          if (!isFocused) {
            event.currentTarget.scrollLeft = 0;
          }
          onScroll?.(event);
        }}
        {...coreProps}
        {...stylex.props(
          textAreaStyles.base,
          inputSizeVariants[size],
          isHovered && textAreaStyles.hovered,
          coreProps.disabled && textAreaStyles.disabled,
          isFocusVisible && textAreaStyles.focused,
          resolveComponentStyle(style, renderState, 'textArea'),
        )}
      />),
    children: (renderProps) => (
      <Provider values={[
        [ProgressIndicatorContext, {
          size: size === 48 ? 20 : 16,
          variant: 'primary',
        }],
        [IconButtonContext, {
          variant: 'ghost',
          size: size === 48 ? 32 : 20,
          isDisabled: coreProps.disabled,
        }],
        [IconContext, {
          color: (coreProps.disabled && 'disabled') || 'tertiary'
        }]]}>
        {children?.(renderProps) || renderProps.defaultChildren}
      </Provider>
    )
  });

  useEffect(() => {
    const textarea = textAreaRef.current;
    if (!textarea) {
      return;
    }

    const resizeTextArea = () => {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    resizeTextArea();

    const observer = new ResizeObserver(resizeTextArea);
    observer.observe(textarea);

    return () => observer.disconnect();
  }, [coreProps.value]);

  return (
    <div
      ref={ref}
      data-focused={isFocused || undefined}
      data-disabled={coreProps.disabled || undefined}
      data-hovered={isHovered || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-invalid={isInvalid || undefined}
      {...pressProps}
      {...hoverProps}
      {...focusProps}
      {...renderProps}
      {...stylex.props(styles.base, sizeVariants[size], variantVariants[variant], resolveComponentStyle(style, renderState, 'base'))}>
      {renderProps.children}
    </div>
  );
});

const styles = stylex.create({
  base: {
    borderStyle: 'solid',
    overflow: 'hidden',
    alignItems: 'center',
    boxSizing: 'border-box',
    columnGap: 12,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
  },
});

const textAreaStyles = stylex.create({
  base: {
    padding: 0,
    borderStyle: 'none',
    flex: '1 1 0',
    outline: 'none',
    alignItems: 'center',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    caretColor: $.textInteractive,
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
    },
    cursor: 'inherit',
    display: 'flex',
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    fontWeight: 400,
    resize: 'none',
    scrollbarWidth: 'none',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
  hovered: { '::placeholder': { color: $.textQuaternary } },
  disabled: { '::placeholder': { color: $.textDisabled } },
  focused: { '::placeholder': { color: $.textPlaceholder } },
});

const variantVariants = stylex.create({
  outlined: {
    borderColor: {
      default: $.border16,
      ':is([data-disabled])': $.border10,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
      ':is([data-hovered]):not([data-focused], [data-focus-within], [data-invalid], [data-disabled])': $.border24,
      ':is([data-invalid])': $.borderCritical,
    },
    borderWidth: {
      default: 1,
      ':is([data-disabled])': 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
    backgroundColor: $.surfaceBGPrimary,
  },
  filled: {
    borderColor: {
      default: 'transparent',
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
    },
    borderWidth: {
      default: 1,
      ':is([data-disabled])': 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
    backgroundColor: {
      default: $.opacity06,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.surfaceBGPrimary,
      ':is([data-invalid])': $.surfaceCriticalSilent,
    },
  },
  underlined: {
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderBottomColor: {
      default: $.border08,
      ':is([data-disabled])': $.border06,
      ':is([data-hovered], [data-focused], [data-focus-within]):not([data-invalid], [data-disabled])': $.borderInteractive,
      ':is([data-invalid])': $.borderCritical,
    },
    borderBottomWidth: 1.5,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
});

const sizeVariants = stylex.create({
  48: {
    borderRadius: 10,
    minHeight: 48,
    paddingBottom: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
    paddingLeft: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingRight: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 13,
    },
    paddingTop: {
      default: 5,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 4,
    },
  },
  32: {
    borderRadius: 8,
    minHeight: 32,
    paddingLeft: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
    paddingRight: {
      default: 12,
      ':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])': 11,
    },
  },
});

const inputSizeVariants = stylex.create({
  48: {
    fontSize: 16,
    letterSpacing: -0.5,
  },
  32: {
    fontSize: 14,
    letterSpacing: -0.45,
  },
});

export { TextArea, TextAreaStyleContext, type TextAreaProps, type TextAreaRenderProps, type TextAreaStyleProps };
